import { create, StoreApi, UseBoundStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { useMemo } from "react";
import {
  PathSegment,
  parseArrayNotation,
  parseNestedPath,
  getNestedValue,
  setNestedValue,
} from "./storePathUtils";
import { getValidateStoreKey } from "../storeKeyUtils";

/**
 * And Store 状态接口
 * 替换原有的 Pinia andStore
 */
export interface AndStoreState {
  // 数据集存储: { [dataSetName]: { [field]: value } }
  dataSets: Record<string, Record<string, any>>;

  // 命令队列存储: { [sessionId]: Command[] }
  commandQueues: Record<string, CommandItem[]>;

  // Props 配置存储: { [sessionId_props]: { [componentId]: props } }
  propsStore: Record<string, Record<string, any>>;

  // 校验结果存储（与 original 一致）: { [sessionId_dataSet_validate]: { [dataSetField]: errorMessage } }
  validateStore: Record<string, Record<string, string>>;
}

export interface CommandItem {
  actionName: string;
  arg?: any;
  timestamp: number;
}

export interface AndStoreActions {
  // 数据集操作（append 默认 true：与已有值浅合并，只更新传入的 key；false 则整体替换）
  // field 传空字符串 '' 时：getDataSet 返回整块对象，setDataSet 操作整块对象，与 getItem/updateItem 语义一致
  setDataSet: (name: string, field: string, value: any, append?: boolean) => void;
  updateDataSet: (name: string, field: string, updater: (prev: any) => any) => void;
  getDataSet: (name: string, field: string) => any;
  setDataSetBatch: (name: string, data: Record<string, any>) => void;
  clearDataSet: (name: string) => void;

  // 命令队列操作
  addCommand: (sessionId: string, command: CommandItem) => void;
  popCommand: (sessionId: string) => CommandItem | undefined;
  clearCommands: (sessionId: string) => void;
  getCommands: (sessionId: string) => CommandItem[];

  // Props 操作
  setProps: (sessionId: string, componentId: string, props: any) => void;
  getProps: (sessionId: string, componentId: string) => any;

  // 校验结果操作（key 由 getValidateStoreKey(sessionId, dataSet) 生成，考虑 sessionId 避免冲突）
  setValidateResult: (sessionId: string | undefined, dataSet: string, dataSetField: string, message: string) => void;
  getValidateResult: (sessionId: string | undefined, dataSet: string, dataSetField: string) => string;
  getValidateResults: (sessionId: string | undefined, dataSet: string) => Record<string, string>;

  // 全局操作
  reset: () => void;
}

export type AndStore = AndStoreState & AndStoreActions;

const initialState: AndStoreState = {
  dataSets: {},
  commandQueues: {},
  propsStore: {},
  validateStore: {},
};

function applyDataSetValue(
  state: AndStoreState,
  name: string,
  fieldPath: PathSegment[],
  resolveValue: (existing: any) => any
) {
  const { baseName, index } = parseArrayNotation(name);

  if (!state.dataSets[baseName]) state.dataSets[baseName] = {};

  if (index !== null) {
    const slice = state.dataSets[baseName];
    let arr = slice.value;
    if (!Array.isArray(arr)) arr = [];
    const element = arr[index];
    const existing =
      fieldPath.length === 0
        ? element
        : getNestedValue(element ?? {}, fieldPath);
    const valueToSet = resolveValue(existing);
    const newElement =
      fieldPath.length === 0
        ? valueToSet
        : setNestedValue(element ?? {}, fieldPath, valueToSet);
    const newArr = [...arr];
    newArr[index] = newElement;
    state.dataSets[baseName] = { ...slice, value: newArr };
    return;
  }

  if (fieldPath.length === 0) {
    state.dataSets[baseName] = resolveValue(state.dataSets[baseName]);
    return;
  }

  let target: any = state.dataSets[baseName];
  for (let i = 0; i < fieldPath.length - 1; i++) {
    const seg = fieldPath[i];
    const nextSeg = fieldPath[i + 1];
    let child = target[seg.key];
    if (child === undefined || child === null) {
      target[seg.key] = nextSeg.type === "array" ? [] : {};
      child = target[seg.key];
    }
    target = child;
  }
  const lastSeg = fieldPath[fieldPath.length - 1];
  const existing = target[lastSeg.key];
  target[lastSeg.key] = resolveValue(existing);
}

/**
 * Zustand Store - 替换 Pinia 的 useItemStore
 */
export const useAndStore: UseBoundStore<StoreApi<AndStore>> =
  create<AndStore>()(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // ============ 数据集操作（支持 name[index] 与 field 多级路径；append 默认 true 为合并更新） ============
        setDataSet: (name, field, value, append = true) =>
          set((state) => {
            if (!name) return;
            const fieldPath = field ? parseNestedPath(field) : [];
            const isMerge = append && typeof value === "object" && value !== null && !Array.isArray(value);

            const mergeIfNeeded = (existing: any): any =>
              isMerge && existing != null && typeof existing === "object" && !Array.isArray(existing)
                ? { ...existing, ...value }
                : value;

            applyDataSetValue(state, name, fieldPath, mergeIfNeeded);
          }),

        updateDataSet: (name, field, updater) =>
          set((state) => {
            if (!name || typeof updater !== "function") return;
            const fieldPath = field ? parseNestedPath(field) : [];
            applyDataSetValue(state, name, fieldPath, updater);
          }),

        getDataSet: (name, field) => {
          if (!name) return undefined;
          const ds = get().dataSets;
          const { baseName, index } = parseArrayNotation(name);
          const root = ds[baseName];

          if (index !== null) {
            const arr = root?.value;
            if (!Array.isArray(arr) || arr[index] === undefined)
              return undefined;
            const item = arr[index];
            if (!field) return item;
            return getNestedValue(item, parseNestedPath(field));
          }

          if (!root) return undefined;
          if (!field) return root;
          return getNestedValue(root, parseNestedPath(field));
        },

        setDataSetBatch: (name, data) =>
          set((state) => {
            state.dataSets[name] = {
              ...state.dataSets[name],
              ...data,
            };
          }),

        clearDataSet: (name) =>
          set((state) => {
            delete state.dataSets[name];
          }),

        // ============ 命令队列操作 ============
        addCommand: (sessionId, command) =>
          set((state) => {
            if (!state.commandQueues[sessionId]) {
              state.commandQueues[sessionId] = [];
            }
            state.commandQueues[sessionId].push(command);
          }),

        popCommand: (sessionId) => {
          const queue = get().commandQueues[sessionId];
          if (queue && queue.length > 0) {
            const first = queue[0];
            set((state) => {
              state.commandQueues[sessionId] =
                state.commandQueues[sessionId].slice(1);
            });
            return first;
          }
          return undefined;
        },

        clearCommands: (sessionId) =>
          set((state) => {
            state.commandQueues[sessionId] = [];
          }),

        getCommands: (sessionId) => get().commandQueues[sessionId] || [],

        // ============ Props 操作 ============
        setProps: (sessionId, componentId, props) =>
          set((state) => {
            const key = `${sessionId}_props`;
            if (!state.propsStore[key]) {
              state.propsStore[key] = {};
            }
            state.propsStore[key][componentId] = props;
          }),

        getProps: (sessionId, componentId) => {
          const key = `${sessionId}_props`;
          return get().propsStore[key]?.[componentId];
        },

        // ============ 校验结果（key 使用 getValidateStoreKey，考虑 sessionId） ============
        setValidateResult: (sessionId, dataSet, dataSetField, message) =>
          set((state) => {
            const key = getValidateStoreKey(sessionId, dataSet);
            if (!key) return;
            if (!state.validateStore[key]) state.validateStore[key] = {};
            state.validateStore[key][dataSetField] = message;
          }),

        getValidateResult: (sessionId, dataSet, dataSetField) => {
          const key = getValidateStoreKey(sessionId, dataSet);
          return key ? get().validateStore[key]?.[dataSetField] ?? "" : "";
        },

        getValidateResults: (sessionId, dataSet) => {
          const key = getValidateStoreKey(sessionId, dataSet);
          return key ? get().validateStore[key] ?? {} : {};
        },

        // ============ 全局操作 ============
        reset: () => set(initialState),
      }))
    )
  );

/**
 * 兼容层：模拟原 useStore 的行为
 * 用于现有组件的渐进式迁移
 */
export function useStore(config: { dataSet?: string; dataSetField?: string }) {
  const { dataSet, dataSetField } = config;

  const setValue = useAndStore((state) => state.setDataSet);

  return {
    get value() {
      return useAndStore.getState().dataSets[dataSet || ""]?.[
        dataSetField || ""
      ];
    },
    set value(newValue: any) {
      if (dataSet && dataSetField) {
        setValue(dataSet, dataSetField, newValue);
      }
    },
  };
}

/**
 * 兼容层：通过名称获取整个数据集
 */
export function useStoreByName(name: string) {
  const setDataSetBatch = useAndStore((state) => state.setDataSetBatch);

  return {
    get value() {
      return useAndStore.getState().dataSets[name];
    },
    set value(newValue: any) {
      setDataSetBatch(name, newValue);
    },
  };
}

/**
 * Hook: 添加命令到队列
 */
export function useAddCommand() {
  return useAndStore((state) => state.addCommand);
}

/**
 * Hook: 获取并消费命令
 */
export function usePopCommand() {
  return useAndStore((state) => state.popCommand);
}

/**
 * 迭代器根数组的结构指纹：仅 id/type/长度，不含正文 content。
 * 用于 useStoreData(dataSetKey, '') 时避免「改一个 input → 整表重渲染」。
 */
function arrayIterationStructureToken(arr: unknown[]): string {
  return arr
    .map((row, index) => {
      if (row == null || typeof row !== "object") return `${index}:${String(row)}`;
      const r = row as { id?: unknown; type?: unknown };
      return `${index}:${String(r.id ?? "")}:${String(r.type ?? "")}`;
    })
    .join("|");
}

function resolveStoreDataArray(
  getDataSet: AndStore["getDataSet"],
  dataSetKey: string,
  field: string,
): unknown[] {
  const data = getDataSet(dataSetKey, field);
  if (Array.isArray(data)) return data;
  if (data != null && typeof data === "object" && Array.isArray((data as { value?: unknown }).value)) {
    return (data as { value: unknown[] }).value;
  }
  return [];
}

/**
 * 按 dataSetKey + dataSetField 从 store 取数组，并订阅对应块以响应更新。
 * 支持多级 dataSetField（如 value、value[0].rows）和 dataSetKey 带下标（如 list[0]）。
 * dataSetField 为空时传 ''，与 getDataSet/setDataSet 一致：读整块对象；再按「整块为数组」或「.value 为数组」规范为数组。
 *
 * 当 field 为空且根数据为数组时，仅订阅结构指纹（行数/id/type），不订阅正文 content；
 * 子组件应通过各自 dataSetField 订阅字段级更新（如 AndInputArea 的 [i].value.content）。
 */
export function useStoreData(dataSetKey: string | undefined, dataSetField: string | undefined): any[] {
  const field =
    dataSetField && String(dataSetField).trim() !== "" ? String(dataSetField) : "";
  const isRootArrayIteration = field === "";

  const chunk = useAndStore((s) => {
    if (!dataSetKey) return undefined;
    const storeKey = parseArrayNotation(dataSetKey).baseName;
    const ds = s.dataSets[storeKey];
    if (!isRootArrayIteration) return ds;
    const arr = Array.isArray(ds)
      ? ds
      : ds != null && typeof ds === "object" && Array.isArray((ds as { value?: unknown[] }).value)
        ? (ds as { value: unknown[] }).value
        : null;
    if (arr) return arrayIterationStructureToken(arr);
    return ds;
  });

  const getDataSet = useAndStore((s) => s.getDataSet);

  return useMemo(() => {
    if (!dataSetKey) return [];
    return resolveStoreDataArray(getDataSet, dataSetKey, field);
  }, [chunk, dataSetKey, field, getDataSet]);
}

/**
 * 兼容层：useItemStore - 模拟 Vue/Pinia 的 useItemStore
 * 用于生成的 React 代码、Extend 等。
 * 不订阅 state.dataSets，避免任意 setDataSet 导致本组件重渲染（解决输入延迟）；
 * items / getItem 通过 getState() 读最新值，子组件若需响应 store 变化请用 useAndStore(selector) 订阅具体 key。
 *
 * 写入推荐用 setDataSet(name, field, value)，可完全替代 updateItem：
 * - setDataSet(key, '', array) 等价于 updateItem(key, array)（field 空为整块对象）
 * - setDataSet 支持多级 field（如 'pagination.current'）、name 下标（如 list[0]），更全面
 * - updateItem 保留用于兼容旧代码
 */
export function useItemStore() {
  const setDataSetBatch = useAndStore((state) => state.setDataSetBatch);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const updateDataSet = useAndStore((state) => state.updateDataSet);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const getState = useAndStore.getState;

  return {
    get items() {
      const dataSets = getState().dataSets;
      return new Proxy(dataSets, {
        get(target, prop: string) {
          return target[prop] || {};
        },
      });
    },
    getDataSet,
    setDataSet,
    updateDataSet,
    updateItem: (key: string, value: any) => {
      const payload =
        value !== null && typeof value === "object" && !Array.isArray(value)
          ? value
          : { value };
      setDataSetBatch(key, payload);
    },
    getItem: (key: string) => {
      return getState().dataSets[key] || {};
    },
  };
}

/**
 * 兼容层：useCommandsStore - 模拟 Vue/Pinia 的 useCommandsStore
 * @param sessionId 会话 ID
 */
export function useCommandsStore(sessionId: string) {
  const addCommand = useAndStore((state) => state.addCommand);
  const popCommand = useAndStore((state) => state.popCommand);
  const getCommands = useAndStore((state) => state.getCommands);
  const clearCommands = useAndStore((state) => state.clearCommands);

  return {
    commands: getCommands(sessionId),
    addCommand: (actionName: string, arg: any) => {
      addCommand(sessionId, {
        actionName,
        arg,
        timestamp: Date.now(),
      });
    },
    popCommand: () => popCommand(sessionId),
    clearCommands: () => clearCommands(sessionId),
  };
}

export default useAndStore;
